import { Children, useEffect, useState, type ReactNode } from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { useInterval } from '@/ui/hooks/useInterval';
import { cn } from '@/ui/lib/utils';
import { sounds } from '@/utils/Sounds';
import './carousel.css';

type EmblaCarouselApi = NonNullable<UseEmblaCarouselType[1]>;

interface CarouselProps {
    children: ReactNode;
    autoPlay?: boolean;
    loop?: boolean;
    className?: string;
}

export const Carousel = ({
    children,
    className,
    loop = false,
    autoPlay = false,
}: CarouselProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop });
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [selectedSnap, setSelectedSnap] = useState(0);
    const [paused, setPaused] = useState(false);
    const slides = Children.toArray(children);

    const goTo = (index: number) => emblaApi?.goTo(index);
    const goToNext = () => emblaApi?.goToNext();

    useEffect(() => {
        if (!emblaApi) return;

        const setupSnaps = (emblaApi: EmblaCarouselApi) => setScrollSnaps(emblaApi.snapList());
        const setActiveSnap = (emblaApi: EmblaCarouselApi) =>
            setSelectedSnap(emblaApi.selectedSnap());

        setupSnaps(emblaApi);
        setActiveSnap(emblaApi);

        emblaApi.on('reinit', setupSnaps);
        emblaApi.on('reinit', setActiveSnap);
        emblaApi.on('select', setActiveSnap);
    }, [emblaApi]);

    useInterval(
        () => {
            goToNext();
        },
        3000,
        autoPlay && !paused,
    );

    return (
        <div className={cn('carousel', className)}>
            <div
                className="carousel__viewport"
                ref={emblaRef}
                onMouseDown={() => setPaused(true)}
                onMouseUp={() => setPaused(false)}
                onMouseLeave={() => setPaused(false)}
                onTouchStart={() => setPaused(true)}
                onTouchEnd={() => setPaused(false)}
            >
                <div className="carousel__container">
                    {slides.map((slide, i) => (
                        <div key={i} className="carousel__slide">
                            {slide}
                        </div>
                    ))}
                </div>
            </div>

            <div className="carousel__dots" role="tablist" aria-label="Slide navigation">
                {scrollSnaps.map((_, i) => (
                    <button
                        key={i}
                        className={cn(
                            'carousel__dot',
                            i === selectedSnap && 'carousel__dot--active',
                        )}
                        onClick={() => {
                            sounds.playFX('click');
                            goTo(i);
                        }}
                        role="tab"
                        aria-selected={i === selectedSnap}
                        aria-label={`Go to slide ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};
